import {
  Table,
  Button,
  Row,
  Col,
  Form,
} from "react-bootstrap";
import { useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useGetProductsQuery } from "../../slices/productsApiSlice";
import { useGetStoreSuppliesQuery } from "../../slices/storeSuppliesApiSlice";
import { useGetStoreSalesQuery } from "../../slices/storeSalesApiSlice";
import { useGetStoreTransfersQuery } from "../../slices/storeTransfersApiSlice";
import { useGetStoreStocksQuery } from "../../slices/storeStocksApiSlice";
import { useGetOpeningStoreStocksQuery } from "../../slices/openingStoreStocksApiSlice";
import { useParams } from "react-router-dom";

const StartStoreStockScreen = () => {
  // State for filtering
  const [filterText, setFilterText] = useState("");

  // Data Fetches
  const { pageNumber } = useParams();
  const { data: productsData } = useGetProductsQuery({pageNumber});
  const { data: storeSuppliesData } = useGetStoreSuppliesQuery();
  const { data: storeSalesData } = useGetStoreSalesQuery();
  const { data: storeTransfersData } = useGetStoreTransfersQuery();
  const { data: storeStocksData } = useGetStoreStocksQuery();
  const { data: openingStoreStocksData } = useGetOpeningStoreStocksQuery();

  // Extract Name & Price
  
  const getProductInfo = () => {
    return productsData?.products?.map((product) => ({
      name: product.name,
      price: product.price || 0,
    })) || [];
  };

  
  // Filter products based on search
  const filteredProducts = getProductInfo().filter((product) =>
    product.name.toLowerCase().includes(filterText.toLowerCase())
  );


  // Calculate Opening Quantity (Earliest StoreStock Date)
  const calculateOpeningQty = (productName) => {
    if (!storeStocksData?.storeStocks) return 0;
    
    const oldestDate = new Date(
      Math.min(...storeStocksData.storeStocks.map((storeStock) => new Date(storeStock.date)))
    );

    return storeStocksData.storeStocks
      .filter((storeStock) => new Date(storeStock.date).getTime() === oldestDate.getTime())
      .reduce((sum, storeStock) => {
        const matchedItems = storeStock.storeStockItems.filter((item) => item.name === productName);
        return sum + matchedItems.reduce((qtySum, item) => qtySum + item.qty, 0);
      }, 0);
  };

  // Calculate Supplied, Sold, and StoreTransferred Quantities
  const calculateTotalSupplied = (productName) =>
    storeSuppliesData?.storeSupplies?.reduce((sum, storeSupply) => {
      const found = storeSupply.storeSupplyItems.filter((item) => item.name === productName);
      return sum + found.reduce((qtySum, item) => qtySum + item.qty, 0);
    }, 0) || 0;

  const calculateTotalStoreTransferred = (productName) =>
    storeTransfersData?.storeTransfers?.reduce((sum, storeTransfer) => {
      const found = storeTransfer.storeTransferItems.filter((item) => item.name === productName);
      return sum + found.reduce((qtySum, item) => qtySum + item.qty, 0);
    }, 0) || 0;

  const calculateTotalSold = (productName) =>
    storeSalesData?.storeSales?.reduce((sum, storeSale) => {
      const found = storeSale.storeSaleItems.filter((item) => item.name === productName);
      return sum + found.reduce((qtySum, item) => qtySum + item.qty, 0);
    }, 0) || 0;

  // Calculate Balance Quantity
  const calculateBalanceQty = (productName) => {
    const openingQty = calculateOpeningQty(productName);
    const totalSupplied = calculateTotalSupplied(productName);
    const totalSold = calculateTotalSold(productName);
    const totalStoreTransferred = calculateTotalStoreTransferred(productName);
    
    return openingQty + totalSupplied - totalSold - totalStoreTransferred;
  };

  // Export to Excel
  const handleExportExcel = () => {
    const exportData = getProductInfo().map((product) => ({
      Product: product.name,
      Price: product.price.toFixed(2),
      "Opening Qty": calculateOpeningQty(product.name),
      "Supplied Qty": calculateTotalSupplied(product.name),
      "Sold Qty": calculateTotalSold(product.name),
      "StoreTransferred Qty": calculateTotalStoreTransferred(product.name),
      "Balance Qty": calculateBalanceQty(product.name),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "StoreStock Report");

    const wbout = XLSX.write(workbook, { type: "array", bookType: "xlsx" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, "storeStock_report.xlsx");
  };

  const calculateAverageCost = (productName) => {
  const openingStoreStockCost = openingStoreStocksData?.storeStocks?.reduce((sum, storeStock) => {
    const matchedItems = storeStock.openingStoreStockItems.filter((item) => item.name === productName);
    return sum + matchedItems.reduce((costSum, item) => costSum + item.itemCost, 0);
  }, 0) || 0;

  const suppliedCost = storeSuppliesData?.storeSupplies?.reduce((sum, storeSupply) => {
    const matchedItems = storeSupply.storeSupplyItems.filter((item) => item.name === productName);
    return sum + matchedItems.reduce((costSum, item) => costSum + item.itemCost, 0);
  }, 0) || 0;

  const openingQty = calculateOpeningQty(productName);
  const totalSupplied = calculateTotalSupplied(productName);
  const totalQty = openingQty + totalSupplied;

  return totalQty > 0 ? (openingStoreStockCost + suppliedCost) / totalQty : 0;
};

const calculateTotalCost = (productName) => {
  const openingStoreStockCost = openingStoreStocksData?.storeStocks?.reduce((sum, storeStock) => {
    const matchedItems = storeStock.openingStoreStockItems.filter((item) => item.name === productName);
    return sum + matchedItems.reduce((costSum, item) => costSum + item.itemCost, 0);
  }, 0) || 0;

  const suppliedCost = storeSuppliesData?.storeSupplies?.reduce((sum, storeSupply) => {
    const matchedItems = storeSupply.storeSupplyItems.filter((item) => item.name === productName);
    return sum + matchedItems.reduce((costSum, item) => costSum + item.itemCost, 0);
  }, 0) || 0;

  return openingStoreStockCost + suppliedCost;
};
const calculateBalanceCost = (productName) => {
  const avgUnitCost = calculateAverageCost(productName);
  const balanceQty = calculateBalanceQty(productName);
  
  return avgUnitCost * balanceQty;
};

const totalOpeningQty = filteredProducts.reduce((sum, product) => sum + calculateOpeningQty(product.name), 0);
const totalSuppliedQty = filteredProducts.reduce((sum, product) => sum + calculateTotalSupplied(product.name), 0);
const totalSoldQty = filteredProducts.reduce((sum, product) => sum + calculateTotalSold(product.name), 0);
const totalStoreTransferredQty = filteredProducts.reduce((sum, product) => sum + calculateTotalStoreTransferred(product.name), 0);
const totalBalanceQty = filteredProducts.reduce((sum, product) => sum + calculateBalanceQty(product.name), 0);
const totalCostSum = filteredProducts.reduce((sum, product) => sum + calculateTotalCost(product.name), 0);
const totalBalanceCost = filteredProducts.reduce((sum, product) => 
  sum + calculateBalanceCost(product.name), 0
);

  return (
    <>
      <Row className="align-items-center mb-4">
        <Col>
          <h1>MiniStock Overview</h1>
        </Col>
        <Row className="mb-3">
    <Col>
    <h4 className="fw-bold">Total Cost: ${totalCostSum.toFixed(2)} Total Bal Cost: ${totalBalanceCost.toFixed(2)}</h4>
    </Col>
   </Row>
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter by product name"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </Col>
        <Col className="text-end">
          <Button variant="success" onClick={handleExportExcel}>
            Export to Excel
          </Button>
        </Col>
      </Row>

      <Table striped bordered hover responsive className="table-sm">
        <thead>
          <tr>
            <th>Product</th>
            <th>Price</th>
            <th>Op Qty</th>
            <th>Sup Qty</th>
            <th>Sold Qty</th>
            <th>Trans Qty</th>
            <th>Bal Qty</th>
            <th>Av Cost</th>
            <th>TotalCost</th>
            <th>Bal Cost</th>
          </tr>
        </thead>
        <tbody>
  {filteredProducts.map((product) => (
    <tr key={product.name}>
      <td>{product.name}</td>
      <td>${product.price.toFixed(2)}</td>
      <td>{calculateOpeningQty(product.name)}</td>
      <td>{calculateTotalSupplied(product.name)}</td>
      <td>{calculateTotalSold(product.name)}</td>
      <td>{calculateTotalStoreTransferred(product.name)}</td>
      <td>{calculateBalanceQty(product.name)}</td>
      <td>${calculateAverageCost(product.name).toFixed(2)}</td>
      <td>${calculateTotalCost(product.name).toFixed(2)}</td>
      <td>${calculateBalanceCost(product.name).toFixed(2)}</td> {/* New Column */}
    </tr>
  ))}
  <tr className="fw-bold">
    <td>Total</td>
    <td>-</td>
    <td>{totalOpeningQty}</td>
    <td>{totalSuppliedQty}</td>
    <td>{totalSoldQty}</td>
    <td>{totalStoreTransferredQty}</td>
    <td>{totalBalanceQty}</td>
    <td>-</td>
    <td>${totalCostSum.toFixed(2)}</td>
    <td>${totalBalanceCost.toFixed(2)}</td> {/* Total Balance Cost */}
  </tr>
</tbody>
      </Table>
    </>
  );
};

export default StartStoreStockScreen;
